import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { fetchUrlImages } from '../../api/posts/fetch-post.api';
import { BrandDispute, BrandSubmissionVersion, MediaRef, openDispute } from '../../api/payment/brand-deal.api';
import { BRAND_DISPUTE_LIMITS, validateDisputeForm } from '../../utils/brand-dispute';
import { formatServerDateTime } from '../../utils/server-date';

// Form creator kháng cáo bài bị từ chối (M4, Task 3) — mô tả + tối đa 5 link + tối đa 5 ảnh (dùng lại
// đúng chỗ tải ảnh thường ở form chiến dịch, `fetchUrlImages`), kèm tóm tắt bằng chứng tự đính kèm (các
// phiên bản đã nộp, kèm giờ nộp — lấy từ chi tiết nhiệm vụ, khỏi phải tự chụp lại).

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export interface BrandDisputeFormProps {
  dealId: string;
  /** Các phiên bản đã nộp của nhiệm vụ — tự đính kèm làm bằng chứng (kèm giờ nộp), khỏi bắt creator tự tải lại. */
  versions: BrandSubmissionVersion[];
  onSubmitted: (dispute: BrandDispute) => void;
  onCancel: () => void;
}

const BrandDisputeForm: React.FC<BrandDisputeFormProps> = ({ dealId, versions, onSubmitted, onCancel }) => {
  const [description, setDescription] = useState('');
  const [links, setLinks] = useState<string[]>(['']);
  const [images, setImages] = useState<MediaRef[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateLink = (idx: number, value: string) => setLinks((prev) => prev.map((l, i) => (i === idx ? value : l)));
  const addLink = () => {
    if (links.length >= BRAND_DISPUTE_LIMITS.maxLinks) return;
    setLinks((prev) => [...prev, '']);
  };
  const removeLink = (idx: number) => setLinks((prev) => prev.filter((_, i) => i !== idx));

  const onPickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = BRAND_DISPUTE_LIMITS.maxImages - images.length;
    if (remaining <= 0) {
      toast.error(`Tối đa ${BRAND_DISPUTE_LIMITS.maxImages} ảnh.`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    try {
      setUploadingImage(true);
      const fd = new FormData();
      picked.forEach((f) => fd.append('type', f));
      const res = (await fetchUrlImages(fd)) as MediaRef[];
      if (res?.length) setImages((prev) => [...prev, ...res.map((r) => ({ id: r.id, url: r.url }))]);
    } catch {
      toast.error('Tải ảnh thất bại.');
    } finally {
      setUploadingImage(false);
    }
  };

  const submit = async () => {
    const cleanLinks = links.map((l) => l.trim()).filter(Boolean);
    const error = validateDisputeForm({ description, links: cleanLinks, images });
    if (error) {
      toast.error(error);
      return;
    }
    if (!window.confirm('Gửi kháng cáo? Doanh nghiệp có 3 ngày để giải trình, sau đó admin sẽ quyết.')) return;
    setSubmitting(true);
    try {
      const dispute = await openDispute(dealId, {
        description: description.trim(),
        evidenceLinks: cleanLinks,
        evidenceImages: images,
      });
      toast.success('Đã gửi kháng cáo.');
      onSubmitted(dispute);
    } catch (e) {
      toast.error(errorMessage(e, 'Gửi kháng cáo thất bại.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <h2 className="ui-heading text-[15px] font-bold">Báo cáo vi phạm / Kháng cáo bản quyền</h2>

      {versions.length > 0 && (
        <div className="rounded-lg border ui-divider p-3 text-[13px]">
          <div className="mb-1 font-semibold ui-text">Bằng chứng tự đính kèm</div>
          <ul className="ui-muted flex flex-col gap-0.5">
            {versions.map((v) => (
              <li key={v.submissionId}>
                Phiên bản {v.version} · nộp lúc {formatServerDateTime(v.submittedAt)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <label className="block text-[13px] ui-text">
        Mô tả kháng cáo ({BRAND_DISPUTE_LIMITS.descriptionMinLength}–
        {BRAND_DISPUTE_LIMITS.descriptionMaxLength.toLocaleString('vi-VN')} ký tự)
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={BRAND_DISPUTE_LIMITS.descriptionMaxLength}
          rows={5}
          className="mt-1 w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
          placeholder="Nêu rõ vì sao bạn cho rằng quyết định từ chối là sai…"
        />
        <span className="mt-1 block text-[11px] ui-muted">
          {description.length}/{BRAND_DISPUTE_LIMITS.descriptionMaxLength}
        </span>
      </label>

      <div className="flex flex-col gap-1.5">
        <div className="text-[13px] ui-text">Link bằng chứng (tối đa {BRAND_DISPUTE_LIMITS.maxLinks})</div>
        {links.map((l, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="flex gap-2">
            <input
              value={l}
              onChange={(e) => updateLink(idx, e.target.value)}
              maxLength={BRAND_DISPUTE_LIMITS.linkMaxLength}
              placeholder="https://..."
              className="w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
            />
            {links.length > 1 && (
              <button
                type="button"
                onClick={() => removeLink(idx)}
                className="rounded-md border ui-divider px-2 text-[13px] ui-muted"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        {links.length < BRAND_DISPUTE_LIMITS.maxLinks && (
          <button type="button" onClick={addLink} className="self-start text-[13px] font-semibold text-[#0057FF]">
            Thêm link
          </button>
        )}
      </div>

      <div>
        <div className="mb-1 text-[13px] ui-text">Ảnh bằng chứng (tối đa {BRAND_DISPUTE_LIMITS.maxImages})</div>
        <div className="flex flex-wrap gap-2">
          {images.map((img) => (
            <div key={img.id || img.url} className="relative size-20 shrink-0 overflow-hidden rounded-lg border ui-divider">
              <img src={img.url} alt="" className="size-full object-cover" />
              <button
                type="button"
                onClick={() => setImages((prev) => prev.filter((x) => x !== img))}
                className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {images.length < BRAND_DISPUTE_LIMITS.maxImages && (
            <label className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider">
              {uploadingImage ? (
                <Loader2 size={18} className="animate-spin ui-muted" />
              ) : (
                <ImagePlus size={18} className="ui-muted" />
              )}
              <input type="file" accept="image/*" multiple hidden onChange={(e) => onPickImages(e.target.files)} />
            </label>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
        >
          {submitting ? 'Đang gửi…' : 'Gửi kháng cáo'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border ui-divider px-4 py-2 text-[14px] font-semibold ui-text"
        >
          Huỷ
        </button>
      </div>
    </div>
  );
};

export default BrandDisputeForm;
